import type { PrerequisiteStatus, PrerequisiteStepStatus, ReadinessCheck, SiteOperatorStatus } from '@/types';
import { formatAge } from '@/lib/boxes';

/** Display names for known prerequisite steps. Display-only: the card renders
 *  whatever steps the backend reports, in this order first, so a step the
 *  backend adds still shows and a step it drops disappears (#844). */
export const PREREQ_LABELS: Record<string, string> = {
  pullSecret: 'Registry Pull Secret',
  siteIdentity: 'Site Identity (OIDC)',
  metricsServer: 'Metrics Server',
  nvidiaDevicePlugin: 'NVIDIA Device Plugin',
  netAttachDefCRD: 'NetworkAttachmentDefinition CRD',
  multus: 'Multus CNI',
  sriovCNI: 'SR-IOV CNI',
  whereaboutsIPAM: 'Whereabouts IPAM',
  longhorn: 'Longhorn Storage',
  ingressNginx: 'Ingress NGINX',
  metallb: 'MetalLB',
  defaultStorageClass: 'Default StorageClass',
};

const PREREQ_STEP_STATUSES: ReadonlySet<string> = new Set(['pending', 'ok', 'skipped', 'failed']);

/** The step entries in a reported PrerequisiteStatus, known steps first. */
export function prerequisiteSteps(prerequisites: PrerequisiteStatus): [string, string, PrerequisiteStepStatus][] {
  const reported = Object.entries(prerequisites)
    .filter((entry): entry is [string, PrerequisiteStepStatus] =>
      typeof entry[1] === 'string' && PREREQ_STEP_STATUSES.has(entry[1]));
  const order = Object.keys(PREREQ_LABELS);
  const rank = (k: string) => (order.includes(k) ? order.indexOf(k) : order.length);
  return reported
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([key, status]) => [key, PREREQ_LABELS[key] ?? key, status]);
}

// ---- Operator path (#805) ----
//
// An operator-path site never writes `prerequisites`: the jss-operator in the
// site cluster installs the components and reports each one as a condition on
// its JBSSiteRequirements CR. What JSS records is the handoff
// (`operatorStatus`): credentials seeded, the pinned operator installed, the
// current spec written. The card shows that handoff, and the per-component
// conditions the PREREQUISITES_READY readiness check reads from the CR.

/** Display names by the CR condition type of each component (jss-operator controllers/conditions.go). */
const CONDITION_LABELS: Record<string, string> = {
  PullSecretReady: PREREQ_LABELS.pullSecret,
  SiteIdentityReady: PREREQ_LABELS.siteIdentity,
  MetricsServerReady: PREREQ_LABELS.metricsServer,
  LonghornReady: PREREQ_LABELS.longhorn,
  IngressNginxReady: PREREQ_LABELS.ingressNginx,
  MetalLBReady: PREREQ_LABELS.metallb,
  NvidiaDevicePluginReady: PREREQ_LABELS.nvidiaDevicePlugin,
  NetAttachDefCRDReady: PREREQ_LABELS.netAttachDefCRD,
  MultusReady: PREREQ_LABELS.multus,
  SriovCNIReady: PREREQ_LABELS.sriovCNI,
  RdmaCNIReady: 'RDMA CNI',
  WhereaboutsReady: PREREQ_LABELS.whereaboutsIPAM,
};

export type OperatorHandoffTone = 'healthy' | 'progress' | 'error';

export interface OperatorHandoff {
  tone: OperatorHandoffTone;
  /** A few words for the badge. */
  label: string;
  /** When it last ran, what happens next. */
  detail: string;
  /** The last pass's error, verbatim; only while failing. */
  error: string | null;
}

function ageMs(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : now - t;
}

/** Where deployment-service's handoff to the site's operator stands. */
export function describeOperatorHandoff(s: SiteOperatorStatus, now: number): OperatorHandoff {
  const attempted = ageMs(s.lastAttemptAt, now);
  if (!s.state || attempted === null) {
    return {
      tone: 'progress',
      label: 'Starting',
      detail: 'Jeket installs the platform operator once the cluster is up; it then installs every component.',
      error: null,
    };
  }

  if (s.state === 'failed') {
    const n = s.consecutiveFailures ?? 1;
    const untilNext = s.nextAttemptAt ? -(ageMs(s.nextAttemptAt, now) ?? 0) : null;
    const retry = untilNext === null ? '' : untilNext > 0 ? ` Retrying in ${formatAge(untilNext)}.` : ' Retrying now.';
    const lastOk = ageMs(s.lastSuccessAt, now);
    return {
      tone: 'error',
      label: 'Failing',
      detail:
        `The last ${n === 1 ? 'attempt' : `${n} attempts`} to hand the site to its operator failed ` +
        `(last ${formatAge(attempted)} ago${lastOk !== null ? `; last succeeded ${formatAge(lastOk)} ago` : ''}).${retry}`,
      error: s.error?.trim() || null,
    };
  }

  // A pool change or Reprovision after the last pass started is still to run.
  const requested = ageMs(s.requestedAt, now);
  if (requested !== null && requested < attempted) {
    return {
      tone: 'progress',
      label: 'Applying a change',
      detail: `A change requested ${formatAge(requested)} ago is being handed to the operator.`,
      error: null,
    };
  }
  return {
    tone: 'healthy',
    label: 'Up to date',
    detail: `The operator has the site's current requirements; checked ${formatAge(attempted)} ago.`,
    error: null,
  };
}

export interface OperatorComponentLine {
  /** Display name, e.g. "Longhorn Storage"; the condition type when unknown. */
  label: string;
  /** The condition's reason and message, e.g. "LonghornNotReady — 2/3 replicas". */
  text: string;
}

/**
 * The components the operator reports not ready, from the PREREQUISITES_READY
 * check's detail: one `<Type>: <reason> — <message>` line per condition that
 * isn't True (siteReadiness.ts). A line in another shape is shown as it is.
 */
export function operatorComponentLines(check: Pick<ReadinessCheck, 'detail'>): OperatorComponentLine[] {
  return (check.detail ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const m = /^([A-Za-z0-9]+):\s*(.*)$/.exec(line);
      if (!m) return { label: line, text: '' };
      return { label: CONDITION_LABELS[m[1]] ?? m[1].replace(/Ready$/, ''), text: m[2] };
    });
}
